/**
 * Orchestrates pulling user data from the backend on login.
 */

import { pullClientsFromBackend } from './clientsService';
import { pullListBuilderFromBackend } from './listBuilderService';
import { pullTripsFromBackend, pushTripsToBackend } from './tripsService';
import {
  activeMemberships,
  listAllTripShares,
} from './tripShareService';
import { pullActiveSharedMemberships } from './sharedTripSyncService';
import { mergeSharedTripRecordsIntoLocalStore } from './sharedTripLocalMerge';
import { setActiveSharedTripIdsFromMemberships } from '../utils/tripAccessPolicy';
import { getPersona } from '../utils/persona';
import { getUserPreferences, flushPreferencesPut } from './userPreferencesSyncService';

const LAST_MANUAL_SYNC_KEY = 'wt_last_manual_sync_at';

export function getLastManualSyncAt(): string | null {
  try {
    return localStorage.getItem(LAST_MANUAL_SYNC_KEY);
  } catch {
    return null;
  }
}

export function setLastManualSyncAt(iso: string): void {
  try {
    localStorage.setItem(LAST_MANUAL_SYNC_KEY, iso);
  } catch {
    // ignore
  }
}

/**
 * Fetch persona-relevant data from the backend into localStorage.
 * Consumer: own Device Sync trips + hydrate share memberships for My Trips visibility.
 * Also pulls /users/me/preferences for both personas (nationality, home TZ, display prefs).
 */
export async function syncUserDataFromBackend(userId: string, email: string): Promise<void> {
  const persona = getPersona();
  const prefsPull = getUserPreferences().catch((err) => {
    console.warn('dataSyncService: preferences pull failed', err);
  });

  if (persona === 'pro') {
    await Promise.all([
      pullClientsFromBackend(userId, email),
      pullListBuilderFromBackend(userId, email),
      prefsPull,
    ]);
    return;
  }
  await Promise.all([pullTripsFromBackend(userId, email), prefsPull]);
  await hydrateTripShareMemberships();
}

/**
 * Settings → Sync now: flush pending prefs PUT, pull backend data, push local trips.
 */
export async function syncNowFromSettings(
  userId: string,
  email: string
): Promise<void> {
  await flushPreferencesPut().catch((err) => {
    console.warn('dataSyncService: preferences flush failed', err);
  });
  await syncUserDataFromBackend(userId, email);
  pushTripsToBackend(userId);
  setLastManualSyncAt(new Date().toISOString());
}

/** Load share roster into access-policy cache (+ best-effort shared payload pull). */
export async function hydrateTripShareMemberships(): Promise<void> {
  try {
    const rows = await listAllTripShares();
    setActiveSharedTripIdsFromMemberships(rows);
    try {
      const pulls = await pullActiveSharedMemberships(activeMemberships(rows));
      for (const pull of pulls) {
        mergeSharedTripRecordsIntoLocalStore(pull.canonicalTripId, pull.records);
      }
    } catch (err) {
      console.warn('dataSyncService: shared trip payload pull failed', err);
    }
  } catch (err) {
    console.warn('dataSyncService: trip share membership hydrate failed', err);
  }
}
